import { defineStore } from "pinia";
import { computed, ref, shallowRef, triggerRef, watch } from "vue";
import { FLAP_VAULTS } from "@/data/vaults.js";
import { readFlapQuotesLive, resolveQuoteSymbol } from "@/lib/flapPortal.js";
import { fetchTokenMeta, needsMeta } from "@/lib/tokenMeta.js";
import { readMonitorListCache, scheduleMonitorListCache } from "@/lib/monitorCache.js";
import {
  createPriceHistory, HISTORY_STORAGE_KEY, CHANGE_FRESH_MS, upstream24h, dex24hQuotes,
} from "@/lib/monitorPriceChanges.js";
import { quoteAssetKind, selectQuoteAssetRates } from "@/lib/quoteAssets.js";
import {
  quoteMcapUsd,
  quoteMcapNative,
  sanitizeQuoteProp,
  quotePriceNativeOk,
  quoteTimestampMs,
  quoteConversionAddress,
  isListedRow,
} from "@/lib/quoteMcap.js";

const Q_CACHE_KEY = "flap_monitor_v2_quotes_v8";
const Q_SOFT_SEC = 5;
const QUOTE_FRESH_MS = 30000;
const ASSET_RATE_FRESH_MS = 120000;
const LIVE_SOFT_MS = 12000;
const VISIBLE_SOFT_MS = 4000;
const MCAP_FIELDS = [
  "priceBnb", "fdvBnb", "mcapBnb", "priceUsd", "fdvUsd", "mcapUsd", "liqUsd",
  "dex", "dexMcap", "pairQuoteSymbol", "pairQuoteToken", "pairQuoteTokenAddress",
  "mcapUpdatedAt", "mcapCached", "mcapStale", "usdFromQuote", "usdBogus", "priceBogus",
  "quoteAssetUsd", "quoteToken", "quoteTokenAddress", "quoteSymbol", "quoteTokenSymbol",
  "source", "priceSource", "quoteUpdatedAt", "stale", "ageSeconds", "circulatingMcapUsd",
  "grossFdvUsd", "grossFdvQuote", "totalSupply", "supplyUpdatedAt", "supplyStale", "valuationSupply", "valuationBasis", "valuationEstimated", "pairAddress", "pairUrl",
];

const VIEW_HASH = {
  latest: "latest",
  today: "today",
  progress: "progress",
  graduated: "graduated",
  changes: "changes",
  heat: "progress",
  mcap: "graduated",
  "today-grad": "today",
};

/** 用 quoteToken 地址纠正错误符号（如 ETH→2170） */
function withResolvedQuoteSymbol(q) {
  if (!q || typeof q !== "object") return q;
  const tok = q.quoteToken || q.quoteTokenAddress || "";
  if (!tok) return q;
  const sym = resolveQuoteSymbol(tok, q.quoteSymbol || q.quoteTokenSymbol);
  if (!sym || sym === q.quoteSymbol) return q;
  return { ...q, quoteSymbol: sym };
}

const GRAD_RECENT_DAYS = 3;
/** 近似 listedAt 比部署晚超过此时长 → 视为 sync 盖「现在」，不可信 */
const APPROX_STALE_MS = 36 * 3600 * 1000;

function recentWindowStart(days = GRAD_RECENT_DAYS) {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - (Math.max(1, days) - 1));
  return start.getTime();
}

/**
 * 近 N 天毕业用的可信时间：
 * - listedExact：用 listedAt
 * - listedAtApprox：仅当部署也在近窗内（避免老币被 sync 写成 now 挤进近3天）
 * - 无 listedAt：部署在近窗内才兜底
 */
function trustedGradAt(row, windowStart) {
  if (!row) return 0;
  const at = Number(row.listedAt) || 0;
  const dep = Number(row.time) || 0;
  const approx = !!(row.listedAtApprox || (at && row.listedExact === false));

  if (at && row.listedExact && !row.listedAtApprox) {
    return at;
  }
  if (at && approx) {
    if (dep && at - dep > APPROX_STALE_MS) return 0;
    if (dep && dep >= windowStart) return at;
    return 0;
  }
  if (at) return at;
  if (dep && dep >= windowStart) return dep;
  return 0;
}

/** 近 N 天内毕业（含今天，按本地日历从 N-1 天前 0 点起） */
function isGraduatedRecent(row, quotesMap, days = GRAD_RECENT_DAYS) {
  if (!isListedRow(row, quotesMap)) return false;
  const startMs = recentWindowStart(days);
  return trustedGradAt(row, startMs) >= startMs;
}

export const useMonitorStore = defineStore("monitor", () => {
  const rows = shallowRef([]);
  const quotes = shallowRef({});
  /** 报价币（股票底）美元价：addr → usd，用于 fdvBnb × 价 */
  const quoteAssetUsd = shallowRef({});
  const quoteAssetUsdAt = shallowRef({});
  const loading = ref(false);
  const quotesLoading = ref(false);
  const liveLoading = ref(false);
  const visibleLoading = ref(false);
  const error = ref("");
  const quoteError = ref("");
  const quotesLastCheckedAt = ref(0);
  const quoteMeta = shallowRef({});
  const listFromCache = ref(false);
  const livePending = ref({});
  /** 软提示：链上 view 失败（不打断列表） */
  const rpcHint = ref("");
  const updatedAt = ref(0);
  const quotesUpdatedAt = ref(0);
  const liveUpdatedAt = ref(0);
  const view = ref("latest");
  const filterId = ref("");
  const lastFresh = ref([]);
  const quoteLive = ref(true); // wagmi 并发 view 可见区
  let etag = "";
  let quotesEtag = "";
  let quotesPromise = null;
  let livePromise = null;
  let listPromise = null;
  let cacheHydrated = false;
  let listHydrated = false;
  let quoteSavePending = false;
  const liveQueue = new Set();
  const liveActive = new Set();
  let liveController = null;
  const lastLiveByToken = new Map();
  const visiblePending = new Set();
  const lastVisibleByToken = new Map();
  let visiblePromise = null;
  let visibleController = null;
  let visibleQueue = null;
  let visibleActiveKey = '';
  let visibleUnavailableUntil = 0;
  let visibleRetryUntil = 0;
  let knownTokens = null; // Set | null；null=尚未建立基线
  let softListBusy = false;
  let enrichBusy = false;
  const enrichSeen = new Set(); // token → 本会话已尝试（避免死循环）
  let enrichSaveTimer = 0;
  const pendingSave = [];
  let assetUsdBusy = false;
  const assetUsdPending = new Set();
  const assetUsdLastAttempt = new Map();
  const quoteAssetSymbols = new Map();
  const priceHistory = createPriceHistory();
  const changeQuotes = shallowRef({});
  const changeClock = ref(0);
  const trackedChangeTokens = new Set();
  const changeLastAttempt = new Map();
  let historyHydrated = false;
  let historyLastSavedAt = 0;
  let changePromise = null;
  let changeQueuedTokens = [];
  let changeRankSeeds = [];
  let changeRankSeedAt = 0;
  let changeRankScope = "";

  function mcapUsdOf(q) {
    const addr = quoteConversionAddress(q);
    const currentAsset = Date.now() - (quoteAssetUsdAt.value[addr] || 0) < ASSET_RATE_FRESH_MS
      ? { [addr]: quoteAssetUsd.value[addr] } : null;
    return quoteMcapUsd(q, currentAsset);
  }

  function priceUsdOf(q) {
    if (!q || q.error || !quotePriceNativeOk(q)) return null;
    const addr = quoteConversionAddress(q);
    const kind = quoteAssetKind(addr, q.pairQuoteSymbol || q.quoteSymbol);
    const direct = Number(q.priceUsd);
    const needsConversion = q.dexMcap && !["bnb", "stable"].includes(kind);
    if (Number.isFinite(direct) && direct > 0 && (!(q.usdBogus || needsConversion) || q.usdFromQuote)) return direct;
    if (Date.now() - (quoteAssetUsdAt.value[addr] || 0) >= ASSET_RATE_FRESH_MS) return null;
    const price = Number(q.priceBnb) * Number(quoteAssetUsd.value[addr]);
    return Number.isFinite(price) && price > 0 ? price : null;
  }

  function mcapNativeOf(q) {
    return quoteMcapNative(q);
  }
  const factoryByAddr = computed(() => {
    const map = {};
    FLAP_VAULTS.forEach((v) => {
      if (!v?.factory) return;
      map[String(v.factory).toLowerCase()] = v;
    });
    return map;
  });

  const needsQuotes = computed(() =>
    ["progress", "graduated", "today", "changes"].includes(view.value)
  );

  function setView(next, { syncHash = true } = {}) {
    const id = VIEW_HASH[next] || next || "latest";
    if (!["latest", "today", "progress", "graduated", "changes"].includes(id)) return;
    view.value = id;
    if (!syncHash || typeof location === "undefined") return;
    const hash = "#" + id;
    // 必须走 vue-router，不能只用 replaceState，否则顶栏 RouterLink 与 route.hash 脱节点不动
    try {
      // 动态取 router，避免 pinia↔router 循环依赖初始化问题
      const router = window.__FLAP_ROUTER__;
      if (router?.currentRoute?.value?.name === "monitor") {
        if (router.currentRoute.value.hash !== hash) {
          router.replace({ hash });
        }
        return;
      }
    } catch {
      /* fall through */
    }
    if (location.hash !== hash) {
      history.replaceState(null, "", location.pathname + location.search + hash);
    }
  }

  function applyHash(hash) {
    const raw = String(hash || "")
      .replace(/^#/, "")
      .trim();
    if (!raw) return;
    // 支持 #latest?sort=mcap（只取视图 id）
    const idPart = raw.split("?")[0].split("&")[0].trim();
    const mapped = VIEW_HASH[idPart];
    if (mapped) setView(mapped, { syncHash: false });
  }

  function applyListedFromQuotes(map) {
    let changed = false;
    const src = rows.value;
    let next = null;
    const savePatches = [];
    for (let i = 0; i < src.length; i++) {
      const row = src[i];
      const tok = String(row.token || "").toLowerCase();
      const q = map[tok];
      if (!q) continue;
      const qListed = !!(q.listed || (q.status != null && Number(q.status) >= 2));
      if (qListed && !row.listed) {
        if (!next) next = src.slice();
        const now = Date.now();
        // 新发现毕业：先记检测时间，避免卡片「毕业 —」；LaunchedToDEX 回填后会覆盖为精确时间
        const patch = {
          ...row,
          listed: true,
          status: q.status != null ? q.status : row.status,
        };
        if (!Number(row.listedAt)) {
          patch.listedAt = now;
          patch.listedDetectedAt = now;
          patch.listedExact = false;
          patch.listedAtApprox = true;
        }
        next[i] = patch;
        changed = true;
        savePatches.push({
          token: tok,
          listed: true,
          status: patch.status,
          listedAt: patch.listedAt,
          listedDetectedAt: patch.listedDetectedAt,
          listedExact: false,
          listedAtApprox: true,
        });
      }
    }
    if (changed) rows.value = next;
    savePatches.forEach((p) => scheduleEnrichSave(p));
  }

  function fetchList(options = {}) {
    hydrateList();
    if (listPromise) {
      // A pushed list change may arrive while another view owns an older read.
      if (options.afterInFlight) return listPromise.then(() => {
        if (options.isActive && !options.isActive()) return { fresh: [], unchanged: true };
        return fetchList({ ...options, afterInFlight: false });
      });
      return listPromise;
    }
    listPromise = fetchListOnce(options).finally(() => { listPromise = null; });
    return listPromise;
  }

  function hydrateList() {
    if (listHydrated) return;
    listHydrated = true;
    if (rows.value.length) return;
    const cached = readMonitorListCache();
    if (!cached) return;
    rows.value = cached.rows;
    updatedAt.value = cached.updatedAt;
    etag = cached.etag;
    listFromCache.value = true;
    // Only a successful network response establishes the notification baseline.
  }

  function saveList() {
    scheduleMonitorListCache({ rows: rows.value, updatedAt: updatedAt.value, etag });
  }

  function hydrateCachedData() {
    hydrateList();
    hydrateQuotes();
  }

  async function fetchListOnce({ soft = false } = {}) {
    if (soft) {
      if (loading.value || softListBusy) return { fresh: [], unchanged: true };
      softListBusy = true;
    } else {
      if (loading.value) return { fresh: [], unchanged: true };
      loading.value = true;
      error.value = "";
    }
    let fresh = [];
    try {
      const headers = {};
      if (etag) headers["If-None-Match"] = etag;
      const res = await fetch("/api/deploys.php?view=list", {
        headers,
        cache: "no-cache",
        signal: AbortSignal.timeout(15000),
      });
      // 首屏 rows 仍空时不要吃 304（浏览器可能自带校验器）
      if (res.status === 304) {
        if (!rows.value.length) {
          etag = "";
          const retry = await fetch("/api/deploys.php?view=list&t=" + Date.now(), {
            cache: "reload",
            signal: AbortSignal.timeout(15000),
          });
          if (!retry.ok) throw new Error("HTTP " + retry.status);
          const nextEtag = retry.headers.get("ETag");
          const json = await retry.json();
          if (!Array.isArray(json.rows)) throw new Error("Invalid list response");
          etag = nextEtag || "";
          const nextRows = json.rows;
          knownTokens = new Set(
            nextRows.map((r) => String(r.token || "").toLowerCase()).filter(Boolean)
          );
          rows.value = nextRows;
          listFromCache.value = false;
          error.value = "";
          updatedAt.value = json.updatedAt || Math.floor(Date.now() / 1000);
          if (Object.keys(quotes.value).length) applyListedFromQuotes(quotes.value);
          saveList();
          return { fresh: [], unchanged: false };
        }
        error.value = "";
        listFromCache.value = false;
        if (!knownTokens) knownTokens = new Set(rows.value.map((r) => String(r.token || "").toLowerCase()));
        saveList();
        return { fresh: [], unchanged: true };
      }
      if (!res.ok) throw new Error("HTTP " + res.status);
      const nextEtag = res.headers.get("ETag");
      const json = await res.json();
      if (!Array.isArray(json.rows)) throw new Error("Invalid list response");
      etag = nextEtag || "";
      const nextRows = json.rows;
      const nextSet = new Set(
        nextRows.map((r) => String(r.token || "").toLowerCase()).filter(Boolean)
      );
      if (knownTokens) {
        fresh = nextRows.filter((r) => {
          const t = String(r.token || "").toLowerCase();
          return t && !knownTokens.has(t);
        });
      }
      knownTokens = nextSet;
      rows.value = nextRows;
      listFromCache.value = false;
      error.value = "";
      updatedAt.value = json.updatedAt || Math.floor(Date.now() / 1000);
      lastFresh.value = fresh;
      if (Object.keys(quotes.value).length) {
        applyListedFromQuotes(quotes.value);
      }
      saveList();
      return { fresh, unchanged: false };
    } catch (e) {
      etag = "";
      error.value = e.message || String(e);
      return { fresh: [], unchanged: true };
    } finally {
      if (soft) softListBusy = false;
      else loading.value = false;
    }
  }

  function hasMcap(q) {
    return mcapUsdOf(q) != null || mcapNativeOf(q) != null;
  }

  function quoteState(token) {
    const tok = String(token || "").toLowerCase();
    const meta = quoteMeta.value[tok] || {};
    const usable = hasMcap(quotes.value[tok]);
    const pending = !!livePending.value[tok] || visiblePending.has(tok) || quotesLoading.value;
    let status = "missing";
    if (usable) {
      status = meta.source !== "cache" && !meta.stale && !meta.error &&
        meta.updatedAt > 0 && Date.now() - meta.updatedAt < (meta.ttl || QUOTE_FRESH_MS)
        ? "fresh" : "stale";
    } else if (pending) status = "loading";
    else if (meta.error || quoteError.value) status = "error";
    return { status, updatedAt: meta.updatedAt || 0, source: meta.source || "" };
  }

  function hydratePriceHistory() {
    if (historyHydrated) return;
    historyHydrated = true;
    try { priceHistory.restore(localStorage.getItem(HISTORY_STORAGE_KEY)); } catch { /* Optional cache. */ }
  }

  function persistPriceHistory() {
    if (!priceHistory.isDirty() || Date.now() - historyLastSavedAt < 60000) return;
    historyLastSavedAt = Date.now();
    try {
      localStorage.setItem(HISTORY_STORAGE_KEY, priceHistory.serialize());
      priceHistory.markSaved();
    } catch { /* Quota errors do not interrupt live prices. */ }
  }

  function recordPriceHistory(tokens) {
    hydratePriceHistory();
    let changed = false;
    for (const token of tokens) {
      const tok = String(token || "").toLowerCase();
      if (!trackedChangeTokens.has(tok) && !priceHistory.has(tok)) continue;
      const state = quoteState(tok);
      if (state.status !== "fresh") continue;
      changed = priceHistory.observe(tok, priceUsdOf(quotes.value[tok]), state.updatedAt) || changed;
    }
    if (changed) changeClock.value++;
    persistPriceHistory();
  }

  function priceChange(token, hours = 24) {
    changeClock.value; // History storage is intentionally non-reactive; batches publish once.
    const tok = String(token || "").toLowerCase();
    const period = Number(hours) === 4 ? 4 : 24;
    const now = Date.now();
    const direct = [upstream24h(quotes.value[tok], now), upstream24h(changeQuotes.value[tok], now)]
      .filter(Boolean).sort((a, b) => b.updatedAt - a.updatedAt)[0];
    if (period === 24 && direct?.status === "ready") return direct;
    const state = quoteState(tok);
    const currentUsd = priceUsdOf(quotes.value[tok]);
    const supplemental = changeQuotes.value[tok];
    const useSupplement = supplemental && now - supplemental.updatedAt < CHANGE_FRESH_MS &&
      (currentUsd == null || state.status !== "fresh" || supplemental.updatedAt > state.updatedAt);
    const local = priceHistory.change(tok,
      useSupplement ? supplemental.priceUsd : currentUsd,
      useSupplement ? supplemental.updatedAt : state.updatedAt,
      period, now, !useSupplement && state.status !== "fresh");
    return period === 24 && local.status !== "ready" && direct?.status === "stale" ? direct : local;
  }

  function priceChanges(token) {
    return { h4: priceChange(token, 4), h24: priceChange(token, 24) };
  }

  function needsChangeSupplement(token, now) {
    return upstream24h(quotes.value[token], now)?.status !== "ready" &&
      upstream24h(changeQuotes.value[token], now)?.status !== "ready" &&
      now - (changeLastAttempt.get(token) || 0) >= CHANGE_FRESH_MS;
  }

  function changeCandidates(visibleTokens) {
    if (view.value !== "changes") return visibleTokens.slice(0, 40);
    const visible = visibleTokens.slice(0, 24);
    const now = Date.now();
    const scope = filterId.value;
    // Keep the seed group stable across reactive re-sorting: one result must not
    // trigger another group and recursively crawl every graduated token.
    if (!changeRankSeedAt || now - changeRankSeedAt >= CHANGE_FRESH_MS || scope !== changeRankScope) {
      const visibleSet = new Set(visible);
      const candidates = filteredRows.value.filter((row) => {
        const token = String(row.token || "").toLowerCase();
        return /^0x[a-f0-9]{40}$/.test(token) && !visibleSet.has(token) &&
          isListedRow(row, quotes.value) && needsChangeSupplement(token, now);
      });
      candidates.sort((a, b) => {
        const ta = String(a.token).toLowerCase();
        const tb = String(b.token).toLowerCase();
        const qa = quotes.value[ta];
        const qb = quotes.value[tb];
        const hasA = hasMcap(qa);
        const hasB = hasMcap(qb);
        if (hasA !== hasB) return hasA ? -1 : 1;
        // Least recently attempted first keeps failed/uncovered candidates fair.
        return (changeLastAttempt.get(ta) || 0) - (changeLastAttempt.get(tb) || 0) ||
          (mcapUsdOf(qb) || 0) - (mcapUsdOf(qa) || 0) || (b.block || 0) - (a.block || 0);
      });
      changeRankSeeds = candidates.slice(0, 40 - visible.length).map((row) => String(row.token).toLowerCase());
      changeRankSeedAt = candidates.length ? now : 0;
      changeRankScope = scope;
    }
    return [...new Set([...visible, ...changeRankSeeds])].slice(0, 40);
  }

  /** Visible rows plus a bounded ranking seed group receive nonblocking supplements. */
  function fetchPriceChanges(tokens) {
    const visible = [...new Set((tokens || []).map((token) => String(token || "").toLowerCase()))]
      .filter((token) => /^0x[a-f0-9]{40}$/.test(token)).slice(0, 40);
    const list = changeCandidates(visible);
    for (const token of list) {
      trackedChangeTokens.delete(token);
      trackedChangeTokens.add(token);
    }
    while (trackedChangeTokens.size > 160) trackedChangeTokens.delete(trackedChangeTokens.values().next().value);
    recordPriceHistory(list);
    changeClock.value++;
    changeQueuedTokens = list;
    if (changePromise) return changePromise;
    changePromise = (async () => {
      while (changeQueuedTokens.length) {
        const candidates = changeQueuedTokens;
        changeQueuedTokens = [];
        const now = Date.now();
        const need = candidates.filter((token) => {
          const q = quotes.value[token];
          const listed = q?.listed || Number(q?.status) >= 2 || rows.value.some((row) => row.token?.toLowerCase() === token && isListedRow(row, quotes.value));
          return listed && needsChangeSupplement(token, now);
        });
        for (let i = 0; i < need.length; i += 20) {
          const chunk = need.slice(i, i + 20);
          const startedAt = Date.now();
          chunk.forEach((token) => changeLastAttempt.set(token, startedAt));
          while (changeLastAttempt.size > 320) changeLastAttempt.delete(changeLastAttempt.keys().next().value);
          try {
            const response = await fetch("https://api.dexscreener.com/latest/dex/tokens/" + chunk.join(","), {
              signal: AbortSignal.timeout(8000),
            });
            if (!response.ok) continue;
            const data = await response.json();
            const cacheAge = Math.max(0, Number(response.headers?.get?.("Age")) || 0) * 1000;
            const serverAt = Date.parse(response.headers?.get?.("Date") || "");
            const at = Math.min(startedAt - cacheAge, Number.isFinite(serverAt) ? serverAt : startedAt);
            const supplement = dex24hQuotes(data.pairs, chunk, quotes.value, at);
            for (const [token, quote] of Object.entries(supplement)) {
              if ((changeQuotes.value[token]?.updatedAt || 0) > at) continue;
              changeQuotes.value[token] = quote;
              priceHistory.observe(token, quote.priceUsd, at);
            }
            const excess = Object.keys(changeQuotes.value).length - 160;
            if (excess > 0) Object.entries(changeQuotes.value)
              .sort((a, b) => a[1].updatedAt - b[1].updatedAt).slice(0, excess)
              .forEach(([token]) => { delete changeQuotes.value[token]; });
            triggerRef(changeQuotes);
            changeClock.value++;
            persistPriceHistory();
          } catch { /* Missing upstream data remains pending, never zero. */ }
        }
      }
    })().finally(() => { changePromise = null; });
    return changePromise;
  }

  function hydrateQuotes() {
    if (cacheHydrated) return;
    cacheHydrated = true;
    try {
      const cached = JSON.parse(localStorage.getItem(Q_CACHE_KEY) || "null");
      if (!cached?.map || typeof cached.map !== "object") return;
      const next = { ...quotes.value };
      const metadata = { ...quoteMeta.value };
      for (const [key, raw] of Object.entries(cached.map)) {
        const tok = key.toLowerCase();
        if (next[tok] || !raw || raw.error) continue;
        next[tok] = sanitizeQuoteProp(withResolvedQuoteSymbol(raw));
        metadata[tok] = {
          updatedAt: quoteTimestampMs(raw.mcapUpdatedAt || cached.meta?.[tok]?.updatedAt || cached.updatedAt),
          source: "cache", stale: true,
        };
      }
      quotes.value = next;
      quoteMeta.value = metadata;
      quotesUpdatedAt.value = quoteTimestampMs(cached.updatedAt) / 1000;
    } catch { /* Invalid storage does not block the request. */ }
  }

  function persistQuotes() {
    if (quoteSavePending) return;
    quoteSavePending = true;
    const save = () => {
      quoteSavePending = false;
      try {
        localStorage.setItem(Q_CACHE_KEY, JSON.stringify({
          at: Date.now(), updatedAt: quotesUpdatedAt.value, map: quotes.value, meta: quoteMeta.value,
        }));
      } catch { /* Storage may be disabled or full. */ }
    };
    if (typeof window === "undefined") save();
    else if (window.requestIdleCallback) window.requestIdleCallback(save, { timeout: 1500 });
    else window.setTimeout(save, 0);
  }

  // Large quote objects stay raw; notify Vue once per completed response/batch.
  function publishQuotes() {
    triggerRef(quotes);
    triggerRef(quoteMeta);
    triggerRef(quoteAssetUsd);
    triggerRef(quoteAssetUsdAt);
    recordPriceHistory(Object.keys(quotes.value));
  }

  function mergeQuote(token, raw, observation, live = false) {
    const tok = token.toLowerCase();
    const previous = quotes.value[tok];
    const previousMeta = quoteMeta.value[tok] || {};
    const incoming = sanitizeQuoteProp(withResolvedQuoteSymbol(raw));
    const oldListed = !!(previous?.listed || Number(previous?.status) >= 2);
    const listed = oldListed || incoming.listed || Number(incoming.status) >= 2;
    const snapshotAt = observation.observedAt || observation.updatedAt || 0;
    const observedAt = Math.max(previousMeta.observedAt || previousMeta.updatedAt || 0, snapshotAt);
    const olderSnapshot = snapshotAt < (previousMeta.observedAt || previousMeta.updatedAt || 0);
    let next = {
      ...(olderSnapshot ? { ...incoming, ...previous } : { ...previous, ...incoming }), listed: !!listed,
      status: listed ? Math.max(2, Number(previous?.status) || 0, Number(incoming.status) || 0) : incoming.status,
    };
    // Missing fields are not evidence of a current USD value or matching DEX unit.
    for (const field of MCAP_FIELDS) {
      if (field in incoming) next[field] = incoming[field];
      else delete next[field];
    }
    const incomingAt = quoteTimestampMs(incoming.mcapUpdatedAt) || observation.updatedAt;
    rememberQuoteRate(incoming, incomingAt, observation.stale);
    const progressOnly = live && oldListed && !incoming.dexMcap && incoming.source !== 'dexscreener';
    const preferVisible = previousMeta.transport === 'visible' && observation.transport !== 'visible' && previousMeta.updatedAt >= incomingAt;
    const keepOld = hasMcap(previous) && (
      !hasMcap(incoming) || previousMeta.updatedAt > incomingAt ||
      progressOnly || preferVisible
    );
    if (keepOld) {
      for (const field of MCAP_FIELDS) {
        if (field in previous) next[field] = previous[field];
        else delete next[field];
      }
      quoteMeta.value[tok] = {
        ...previousMeta, observedAt,
        stale: !!previousMeta.stale || (!progressOnly && !hasMcap(incoming) && !(previousMeta.updatedAt > incomingAt)),
      };
    } else {
      quoteMeta.value[tok] = {
        ...observation, observedAt, updatedAt: incomingAt,
        stale: !!(observation.stale || incoming.stale || incoming.mcapStale || (incoming.mcapCached && observation.transport !== 'visible')), error: "",
      };
    }
    applyQuoteAssetSymbol(next);
    quotes.value[tok] = sanitizeQuoteProp(withResolvedQuoteSymbol(next));
  }

  function applyQuoteAssetSymbol(q) {
    const placeholder = (symbol) => !symbol || /^(?:0x)?[a-f0-9]{4,8}$/i.test(String(symbol).trim());
    const original = String(q.quoteToken || q.quoteTokenAddress || "").toLowerCase();
    const originalSymbol = quoteAssetSymbols.get(original);
    if (originalSymbol && placeholder(q.quoteSymbol)) q.quoteSymbol = originalSymbol;
    const pair = String(q.pairQuoteToken || q.pairQuoteTokenAddress || "").toLowerCase();
    const pairSymbol = quoteAssetSymbols.get(pair);
    if (pairSymbol && placeholder(q.pairQuoteSymbol)) q.pairQuoteSymbol = pairSymbol;
  }

  function rememberQuoteRate(q, updatedAt, stale) {
    if (stale || q.mcapCached || q.mcapStale || q.usdBogus || q.priceBogus ||
      !updatedAt || Date.now() - updatedAt >= ASSET_RATE_FRESH_MS) return;
    const addr = quoteConversionAddress(q);
    if (q.dexMcap && !["bnb", "stable"].includes(quoteAssetKind(addr, q.pairQuoteSymbol || q.quoteSymbol)) && !q.usdFromQuote) return;
    const native = Number(q.priceBnb);
    const usd = Number(q.priceUsd);
    const rate = usd / native;
    if (!/^0x[a-f0-9]{40}$/.test(addr) || !(native > 0) || !(rate > 0) ||
      !Number.isFinite(rate) || !(mcapUsdOf(q) > 0)) return;
    if ((quoteAssetUsdAt.value[addr] || 0) > updatedAt) return;
    quoteAssetUsd.value[addr] = rate;
    quoteAssetUsdAt.value[addr] = updatedAt;
  }

  function markQuoteFailure(tokens, message, startedAt) {
    for (const tok of tokens) {
      const old = quoteMeta.value[tok] || {};
      if (old.updatedAt > startedAt) continue;
      quoteMeta.value[tok] = { ...old, stale: true, error: message };
    }
  }

  function fetchQuotes({ soft = true } = {}) {
    if (quotesPromise) return quotesPromise;
    hydrateQuotes();
    if (soft && quotesLastCheckedAt.value && Date.now() - quotesLastCheckedAt.value < Q_SOFT_SEC * 1000) {
      return Promise.resolve();
    }
    const startedAt = Date.now();
    quotesLoading.value = true;
    quotesPromise = (async () => {
      try {
        const res = await fetch("/api/quotes.php", {
          headers: quotesEtag ? { "If-None-Match": quotesEtag } : {},
          cache: "no-cache", signal: AbortSignal.timeout(15000),
        });
        if (res.status === 304 && quotesEtag) {
          quoteError.value = "";
          return;
        }
        if (!res.ok) throw new Error("Quotes HTTP " + res.status);
        const json = await res.json();
        if (!json.quotes || typeof json.quotes !== "object" || Array.isArray(json.quotes)) {
          throw new Error(json.error || "Invalid quotes response");
        }
        if (json.error) throw new Error(String(json.error));
        quotesEtag = res.headers?.get?.("ETag") || "";
        const observedAt = quoteTimestampMs(json.updatedAt);
        const ttl = Number(json.ttl) > 0 ? Math.min(Number(json.ttl) * 1000, QUOTE_FRESH_MS) : QUOTE_FRESH_MS;
        const returned = new Set();
        for (const [key, raw] of Object.entries(json.quotes)) {
          const tok = key.toLowerCase();
          if (!raw || raw.error) {
            if (quoteMeta.value[tok]?.transport !== 'visible') markQuoteFailure([tok], raw?.error || "Quote unavailable", startedAt);
            continue;
          }
          returned.add(tok);
          mergeQuote(tok, raw, {
            updatedAt: (raw.listed || Number(raw.status) >= 2)
              ? quoteTimestampMs(json.mcapUpdatedAt) || observedAt : observedAt,
            source: raw.source === 'dexscreener' ? 'dexscreener' : raw.source === 'portal' ? 'portal' : "server", ttl, observedAt,
            stale: !!json.stale || !observedAt || Date.now() - observedAt >= ttl,
          });
        }
        // Keep omitted/new tokens; only observations covered by this snapshot go stale.
        for (const tok of Object.keys(quotes.value)) {
          const meta = quoteMeta.value[tok] || {};
          if (!returned.has(tok) && meta.transport !== 'visible' && !(meta.updatedAt > observedAt)) {
            quoteMeta.value[tok] = { ...meta, stale: true };
          }
        }
        quotesUpdatedAt.value = observedAt / 1000;
        quoteError.value = "";
        publishQuotes();
        applyListedFromQuotes(quotes.value);
        void enrichQuoteAssetUsd();
        persistQuotes();
      } catch (e) {
        quotesEtag = "";
        quoteError.value = e?.message || String(e);
        markQuoteFailure(Object.keys(quotes.value).filter(tok => quoteMeta.value[tok]?.transport !== 'visible' && quoteMeta.value[tok]?.source !== 'rpc'), quoteError.value, startedAt);
        triggerRef(quoteMeta);
      } finally {
        quotesLastCheckedAt.value = Date.now();
        quotesLoading.value = false;
        quotesPromise = null;
      }
    })();
    return quotesPromise;
  }

  function clearLiveQueue() {
    for (const tok of liveQueue) delete livePending.value[tok];
    for (const tok of liveActive) delete livePending.value[tok];
    liveQueue.clear();
    liveController?.abort();
  }

  function clearVisibleQuotes() {
    visibleQueue = null;
    visibleController?.abort();
    visiblePending.clear();
    triggerRef(quoteMeta);
  }

  /** WebSocket payloads use the same source-time and valuation merge rules as visible HTTP reads. */
  function applyPushedQuotes(payload, tokens) {
    if (!payload?.quotes || typeof payload.quotes !== 'object' || Array.isArray(payload.quotes)) return false;
    const allowed = new Set((tokens || []).map(token => String(token).toLowerCase()));
    const changed = {};
    let usable = false;
    for (const [key, q] of Object.entries(payload.quotes)) {
      const tok = key.toLowerCase();
      if (!allowed.has(tok) || !/^0x[a-f0-9]{40}$/.test(tok) || !q || q.error || typeof q !== 'object' || Array.isArray(q)) continue;
      const at = quoteTimestampMs(q.mcapUpdatedAt || q.quoteUpdatedAt || payload.updatedAt);
      const stale = !!(q.stale === true || (q.stale == null && payload.stale) || q.mcapStale || !at || Date.now() - at >= QUOTE_FRESH_MS);
      mergeQuote(tok, q, { updatedAt: at, observedAt: at, source: q.source || q.priceSource || 'server',
        transport: 'visible', ttl: QUOTE_FRESH_MS, stale }, true);
      changed[tok] = quotes.value[tok];
      if (hasMcap(q) && !stale && at > 0 && at <= Date.now() + 2000) usable = true;
    }
    if (Object.keys(changed).length) {
      publishQuotes(); applyListedFromQuotes(changed); persistQuotes();
      liveUpdatedAt.value = Math.floor(Date.now() / 1000);
      rpcHint.value = Object.values(changed).some(q => !hasMcap(q)) ? 'Some visible quotes are unavailable' : '';
    }
    return usable;
  }

  watch(view, (next) => {
    clearVisibleQuotes();
    if (next === "changes") clearLiveQueue();
  }, { flush: "sync" });

  /** One visible page, serialized twenty-token HTTP batches. Old-page responses never publish. */
  function fetchVisibleQuotes(tokens, { force = false } = {}) {
    if (!quoteLive.value) return Promise.resolve();
    if (Date.now() < visibleRetryUntil) return Promise.resolve();
    const list = [...new Set((tokens || []).map(t => String(t || '').toLowerCase()))]
      .filter(t => /^0x[a-f0-9]{40}$/.test(t)).slice(0, 24);
    const mode = view.value === 'changes' ? 'dex' : 'all';
    const key = mode + ':' + [...list].sort().join(',');
    if (visiblePromise && visibleActiveKey === key && !visibleController?.signal.aborted) return visiblePromise;
    if (visibleController && visibleActiveKey !== key) {
      visibleController.abort();
      visiblePending.clear();
      clearLiveQueue();
    }
    const todo = list.filter(tok => force || Date.now() - (lastVisibleByToken.get(mode + ':' + tok) || 0) >= VISIBLE_SOFT_MS);
    visibleQueue = todo.length ? { tokens: todo, key, mode } : null;
    todo.forEach(tok => visiblePending.add(tok));
    triggerRef(quoteMeta);
    if (visiblePromise) return visiblePromise;
    if (!visibleQueue) return Promise.resolve();
    visibleLoading.value = true;
    visiblePromise = (async () => {
      while (visibleQueue) {
        const job = visibleQueue;
        visibleQueue = null;
        visibleActiveKey = job.key;
        const controller = new AbortController();
        visibleController = controller;
        const fallback = new Set();
        const rpcFallback = new Set();
        for (let offset = 0; offset < job.tokens.length && !controller.signal.aborted; offset += 20) {
          const batch = job.tokens.slice(offset, offset + 20);
          const startedAt = Date.now();
          let backgroundRefresh = false;
          try {
            if (Date.now() < visibleUnavailableUntil) throw new Error('Live quotes temporarily unavailable');
            const res = await fetch('/api/quotes_live.php?tokens=' + batch.join(',') + (job.mode === 'dex' ? '&mode=dex' : ''), {
              cache: 'no-store', signal: AbortSignal.any([controller.signal, AbortSignal.timeout(18000)]),
            });
            if (!res.ok) {
              visibleUnavailableUntil = Date.now() + ([404, 405, 501].includes(res.status) ? 60000 : 10000);
              throw new Error('Live quotes HTTP ' + res.status);
            }
            const json = await res.json();
            if (controller.signal.aborted) break;
            if (!json.quotes || typeof json.quotes !== 'object' || Array.isArray(json.quotes)) throw new Error('Invalid live quotes response');
            backgroundRefresh = !!(json.refreshPending || json.refreshInProgress);
            if (json.rateLimited || json.refreshInProgress) {
              visibleRetryUntil = Date.now() + Math.min(60000, Math.max(1000, (Number(json.retryAfter) || 8) * 1000));
            }
            const changed = {};
            const returned = Object.fromEntries(Object.entries(json.quotes).map(([token, q]) => [token.toLowerCase(), q]));
            for (const tok of batch) {
              const q = returned[tok];
              if (!q || q.error) { fallback.add(tok); continue; }
              const at = quoteTimestampMs(q.mcapUpdatedAt || q.quoteUpdatedAt || json.updatedAt);
              const stale = !!(q.stale === true || (q.stale == null && json.stale) || q.mcapStale || !at || Date.now() - at >= QUOTE_FRESH_MS);
              mergeQuote(tok, q, { updatedAt: at, observedAt: at, source: q.source || q.priceSource || 'server',
                transport: 'visible', ttl: QUOTE_FRESH_MS, stale }, true);
              changed[tok] = quotes.value[tok];
              if (stale || !hasMcap(q)) fallback.add(tok);
              lastVisibleByToken.set(job.mode + ':' + tok, backgroundRefresh ? Date.now() - VISIBLE_SOFT_MS : Date.now());
              visiblePending.delete(tok);
            }
            if (Object.keys(changed).length) {
              publishQuotes(); applyListedFromQuotes(changed); persistQuotes();
              liveUpdatedAt.value = Math.floor(Date.now() / 1000);
            }
            rpcHint.value = fallback.size ? 'Some visible quotes are unavailable' : '';
            if (json.rateLimited || json.refreshInProgress) break;
          } catch (e) {
            if (controller.signal.aborted) break;
            batch.forEach(tok => { fallback.add(tok); rpcFallback.add(tok); });
            rpcHint.value = e?.message || 'Live quotes unavailable';
          } finally {
            if (!controller.signal.aborted) {
              batch.forEach(tok => { visiblePending.delete(tok); lastVisibleByToken.set(job.mode + ':' + tok, backgroundRefresh ? Date.now() - VISIBLE_SOFT_MS : Date.now()); });
              triggerRef(quoteMeta);
            }
          }
        }
        if (!controller.signal.aborted && fallback.size) {
          void fetchQuotes({ soft: true });
          // The changes tab never triggers Portal RPC, including when the endpoint is absent.
          if (job.mode !== 'dex') {
            const inner = [...rpcFallback].filter(tok => !isListedRow(rows.value.find(r => String(r.token).toLowerCase() === tok) || { token: tok }, quotes.value));
            if (inner.length) void fetchLiveQuotes(inner, { replaceQueue: true });
          }
        }
        if (!controller.signal.aborted) { job.tokens.forEach(tok => visiblePending.delete(tok)); triggerRef(quoteMeta); }
        visibleController = null;
      }
    })().finally(() => { visibleLoading.value = false; visiblePromise = null; visibleActiveKey = ''; });
    return visiblePromise;
  }

  /** Visible tokens replace obsolete queued work; successful batches publish immediately. */
  function fetchLiveQuotes(tokens, { force = false, replaceQueue = false } = {}) {
    if (view.value === "changes") {
      clearLiveQueue();
      return Promise.resolve();
    }
    if (!quoteLive.value) return Promise.resolve();
    const list = [...new Set((tokens || []).map((t) => String(t || "").toLowerCase()))]
      .filter((t) => /^0x[a-f0-9]{40}$/.test(t));
    if (replaceQueue) {
      for (const tok of liveQueue) {
        if (!liveActive.has(tok)) delete livePending.value[tok];
      }
      liveQueue.clear();
      if (liveActive.size && !list.slice(0, 24).some((tok) => liveActive.has(tok) && livePending.value[tok])) liveController?.abort();
    }
    for (const tok of list) {
      if (liveActive.has(tok) && !liveController?.signal.aborted) continue;
      if (!force && Date.now() - (lastLiveByToken.get(tok) || 0) < LIVE_SOFT_MS) continue;
      liveQueue.add(tok);
      livePending.value[tok] = true;
    }
    if (livePromise) return livePromise;
    if (!liveQueue.size) return Promise.resolve();
    liveLoading.value = true;
    livePromise = (async () => {
      while (liveQueue.size) {
        const batch = [...liveQueue].slice(0, 24);
        batch.forEach((tok) => { liveQueue.delete(tok); liveActive.add(tok); });
        const controller = new AbortController();
        liveController = controller;
        const startedAt = Date.now();
        const accepted = new Set();
        const acceptBatch = (partial) => {
          if (controller.signal.aborted) return;
          const changed = {};
          for (const [tok, q] of Object.entries(partial)) {
            if (!batch.includes(tok) || accepted.has(tok) || !q || q.error) continue;
            mergeQuote(tok, q, {
              updatedAt: quoteTimestampMs(q.quoteUpdatedAt) || (q.source === "wagmi" ? Date.now() : 0),
              source: q.source === "wagmi" ? "rpc" : "server", ttl: QUOTE_FRESH_MS,
              stale: !!q.quoteStale || (q.source !== "wagmi" && !q.quoteUpdatedAt),
            }, true);
            accepted.add(tok);
            changed[tok] = quotes.value[tok];
            delete livePending.value[tok];
            lastLiveByToken.set(tok, Date.now());
          }
          if (!Object.keys(changed).length) return;
          publishQuotes();
          liveUpdatedAt.value = Math.floor(Date.now() / 1000);
          applyListedFromQuotes(changed);
          void enrichQuoteAssetUsd(Object.keys(changed));
          persistQuotes();
        };
        try {
          const live = await readFlapQuotesLive(batch, {
            chunkSize: 8, concurrency: 3, timeoutMs: 22000,
            onBatch: acceptBatch, signal: controller.signal,
          });
          acceptBatch(live);
          if (controller.signal.aborted) continue;
          const missing = batch.filter((tok) => !accepted.has(tok));
          if (missing.length) markQuoteFailure(missing, "Quote unavailable", startedAt);
          missing.forEach((tok) => lastLiveByToken.set(tok, Date.now()));
          rpcHint.value = missing.length ? "Some visible quotes are unavailable" : "";
          triggerRef(quoteMeta);
        } catch (e) {
          if (!controller.signal.aborted) {
            rpcHint.value = e?.message || String(e || "RPC unavailable");
            const missing = batch.filter((tok) => !accepted.has(tok));
            markQuoteFailure(missing, rpcHint.value, startedAt);
            missing.forEach((tok) => lastLiveByToken.set(tok, Date.now()));
            triggerRef(quoteMeta);
          }
        } finally {
          batch.forEach((tok) => {
            if (!liveQueue.has(tok)) delete livePending.value[tok];
            liveActive.delete(tok);
          });
          liveController = null;
        }
      }
    })().finally(() => {
      liveLoading.value = false;
      livePromise = null;
    });
    return livePromise;
  }

  const filteredRows = computed(() => {
    const qmap = quotes.value;
    const fmap = factoryByAddr.value;
    let list = rows.value.slice();
    if (filterId.value) {
      list = list.filter((r) => {
        const tpl = fmap[String(r.factory || "").toLowerCase()];
        return tpl && tpl.id === filterId.value;
      });
    }
    if (view.value === "progress") {
      list = list.filter((r) => !isListedRow(r, qmap));
      list.sort((a, b) => {
        const qa = qmap[String(a.token || "").toLowerCase()];
        const qb = qmap[String(b.token || "").toLowerCase()];
        const ha = qa && qa.progress != null && isFinite(qa.progress);
        const hb = qb && qb.progress != null && isFinite(qb.progress);
        if (ha !== hb) return ha ? -1 : 1;
        if (!ha) return (b.block || 0) - (a.block || 0);
        return qb.progress - qa.progress;
      });
    } else if (view.value === "today") {
      const startMs = recentWindowStart(GRAD_RECENT_DAYS);
      list = list.filter((r) => {
        if (!isListedRow(r, qmap)) return false;
        return trustedGradAt(r, startMs) >= startMs;
      });
      list.sort((a, b) => {
        const ka = trustedGradAt(a, startMs) || Number(a.listedAt) || 0;
        const kb = trustedGradAt(b, startMs) || Number(b.listedAt) || 0;
        if (kb !== ka) return kb - ka;
        return (b.block || 0) - (a.block || 0);
      });
    } else if (view.value === "graduated") {
      list = list.filter((r) => isListedRow(r, qmap));
      list.sort((a, b) => {
        const qa = qmap[String(a.token || "").toLowerCase()];
        const qb = qmap[String(b.token || "").toLowerCase()];
        const ma = mcapUsdOf(qa);
        const mb = mcapUsdOf(qb);
        const ha = ma != null;
        const hb = mb != null;
        if (ha !== hb) return ha ? -1 : 1;
        if (ha) return mb - ma;
        // 无美元：按报价币 FDV 排（股票底）
        const na = Number(qa?.fdvBnb || qa?.mcapBnb) || 0;
        const nb = Number(qb?.fdvBnb || qb?.mcapBnb) || 0;
        if (na > 0 || nb > 0) return nb - na;
        const ka = a.listedAt || a.time || a.block || 0;
        const kb = b.listedAt || b.time || b.block || 0;
        return kb - ka;
      });
    } else {
      list.sort((a, b) => (b.block || 0) - (a.block || 0));
    }
    return list;
  });

  /**
   * 链上 live 刷新目标。
   * 内盘：除进度榜前列外，再带上最近部署——避免缓存进度卡在 0.00x% 时永远排在末尾、得不到刷新。
   */
  function liveQuoteTokens({ visibleLimit = 12, recentLimit = 0 } = {}) {
    const qmap = quotes.value;
    const out = [];
    const seen = new Set();
    const pushTok = (token) => {
      const t = String(token || "").toLowerCase();
      if (!/^0x[a-f0-9]{40}$/.test(t) || seen.has(t)) return;
      seen.add(t);
      out.push(t);
    };
    for (const r of filteredRows.value.slice(0, Math.max(0, visibleLimit))) {
      pushTok(r.token);
    }
    const recentN =
      view.value === "progress" ? Math.max(0, recentLimit) : 0;
    if (recentN > 0) {
      const recent = rows.value
        .filter((r) => !isListedRow(r, qmap))
        .sort(
          (a, b) =>
            (b.block || 0) - (a.block || 0) || (b.time || 0) - (a.time || 0)
        )
        .slice(0, recentN);
      for (const r of recent) pushTok(r.token);
    }
    return out;
  }

  const factoryCounts = computed(() => {
    const map = new Map();
    for (const r of rows.value) {
      const addr = String(r.factory || "").toLowerCase();
      if (!addr) continue;
      map.set(addr, (map.get(addr) || 0) + 1);
    }
    return map;
  });

  function setFilterId(id) {
    filterId.value = id || "";
  }

  function countByFactory(factory) {
    const addr = String(factory || "").toLowerCase();
    if (!addr) return 0;
    return factoryCounts.value.get(addr) || 0;
  }

  function scheduleEnrichSave(patch) {
    pendingSave.push(patch);
    if (enrichSaveTimer) return;
    enrichSaveTimer = window.setTimeout(async () => {
      enrichSaveTimer = 0;
      const batch = pendingSave.splice(0, pendingSave.length);
      if (!batch.length) return;
      try {
        await fetch("/api/deploys.php", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ rows: batch }),
        });
      } catch {
        /* ignore */
      }
    }, 1500);
  }

  function patchRowMeta(token, meta) {
    const tok = String(token || "").toLowerCase();
    let changed = false;
    const next = rows.value.map((row) => {
      if (String(row.token || "").toLowerCase() !== tok) return row;
      const patch = { ...row };
      if (!patch.name && meta.name) {
        patch.name = meta.name;
        changed = true;
      }
      if (!patch.symbol && meta.symbol) {
        patch.symbol = meta.symbol;
        changed = true;
      }
      if (!patch.image && meta.image) {
        patch.image = meta.image;
        changed = true;
      }
      if (!patch.meta && meta.meta) {
        patch.meta = meta.meta;
        changed = true;
      }
      return patch;
    });
    if (changed) {
      rows.value = next;
      scheduleEnrichSave({
        token: tok,
        name: meta.name || "",
        symbol: meta.symbol || "",
        image: meta.image || "",
        meta: meta.meta || "",
      });
    }
    return changed;
  }

  /** Refresh quote-asset rates for current native amounts, including new bonding tokens. */
  async function enrichQuoteAssetUsd(tokens) {
    if (assetUsdBusy) {
      for (const token of tokens || []) assetUsdPending.add(token);
      return;
    }
    const need = [];
    const seen = new Set();
    const candidates = tokens || rows.value.map((r) => r.token);
    for (const token of candidates) {
      const q = quotes.value[String(token || "").toLowerCase()];
      if (!q || mcapUsdOf(q) != null) continue;
      const addr = quoteConversionAddress(q);
      if (!/^0x[a-f0-9]{40}$/.test(addr) || seen.has(addr)) continue;
      if (Number(quoteAssetUsd.value[addr]) > 0 && Date.now() - (quoteAssetUsdAt.value[addr] || 0) < ASSET_RATE_FRESH_MS) continue;
      if (Date.now() - (assetUsdLastAttempt.get(addr) || 0) < 30000) continue;
      seen.add(addr);
      need.push(addr);
    }
    if (!need.length) return;
    assetUsdBusy = true;
    try {
      for (let i = 0; i < need.length; i += 20) {
        const chunk = need.slice(i, i + 20);
        const startedAt = Date.now();
        chunk.forEach((address) => assetUsdLastAttempt.set(address, startedAt));
        try {
          const res = await fetch(
            "https://api.dexscreener.com/latest/dex/tokens/" + chunk.join(","),
            { signal: AbortSignal.timeout(10000) }
          );
          if (!res.ok) continue;
          const data = await res.json();
          const best = selectQuoteAssetRates(data.pairs, chunk);
          const at = startedAt - Math.max(0, Number(res.headers?.get?.("Age")) || 0) * 1000;
          Object.keys(best).forEach((a) => {
            if ((quoteAssetUsdAt.value[a] || 0) > at) return;
            quoteAssetUsd.value[a] = best[a].price;
            quoteAssetUsdAt.value[a] = at;
            if (best[a].symbol) quoteAssetSymbols.set(a, best[a].symbol);
          });
          for (const q of Object.values(quotes.value)) applyQuoteAssetSymbol(q);
          triggerRef(quotes);
          triggerRef(quoteAssetUsd);
          triggerRef(quoteAssetUsdAt);
          recordPriceHistory(candidates);
        } catch {
          /* ignore chunk */
        }
        if (i + 20 < need.length) {
          await new Promise((r) => setTimeout(r, 250));
        }
      }
    } finally {
      assetUsdBusy = false;
      if (assetUsdPending.size) {
        const pending = [...assetUsdPending];
        assetUsdPending.clear();
        void enrichQuoteAssetUsd(pending);
      }
    }
  }

  /** 可见区 / 新发：链上补 name·symbol·头像（经典站同款） */
  async function enrichMissing(list, { force = false, limit = 12 } = {}) {
    if (enrichBusy) return;
    const candidates = (list || [])
      .filter((r) => r && needsMeta(r))
      .slice(0, limit);
    if (!candidates.length) return;
    enrichBusy = true;
    try {
      for (const row of candidates) {
        const tok = String(row.token || "").toLowerCase();
        if (!tok) continue;
        if (!force && enrichSeen.has(tok)) continue;
        enrichSeen.add(tok);
        const meta = await fetchTokenMeta(tok);
        if (meta.name || meta.symbol || meta.image) {
          patchRowMeta(tok, meta);
          // 仍缺图则允许下一轮再试
          const row2 = rows.value.find(
            (r) => String(r.token || "").toLowerCase() === tok
          );
          if (row2 && needsMeta(row2) && !meta.image) {
            enrichSeen.delete(tok);
          }
        } else {
          // 彻底没 meta 的，过一段时间再试（滚动窗口）
          window.setTimeout(() => enrichSeen.delete(tok), 120000);
        }
        await new Promise((r) => setTimeout(r, 80));
      }
    } finally {
      enrichBusy = false;
    }
  }

  return {
    rows,
    quotes,
    loading,
    quotesLoading,
    liveLoading,
    visibleLoading,
    error,
    quoteError,
    quotesLastCheckedAt,
    listFromCache,
    hydrateCachedData,
    clearLiveQueue,
    clearVisibleQuotes,
    quoteState,
    priceChange,
    priceChanges,
    fetchPriceChanges,
    rpcHint,
    updatedAt,
    quotesUpdatedAt,
    liveUpdatedAt,
    view,
    filterId,
    lastFresh,
    quoteLive,
    needsQuotes,
    filteredRows,
    factoryByAddr,
    factoryCounts,
    fetchList,
    fetchQuotes,
    fetchLiveQuotes,
    fetchVisibleQuotes,
    applyPushedQuotes,
    liveQuoteTokens,
    enrichMissing,
    enrichQuoteAssetUsd,
    setView,
    applyHash,
    setFilterId,
    countByFactory,
    quoteMcapUsd: mcapUsdOf,
    quotePriceUsd: priceUsdOf,
    quoteMcapNative: mcapNativeOf,
    isListedRow: (r) => isListedRow(r, quotes.value),
    isGraduatedToday: (r) => isGraduatedRecent(r, quotes.value),
    isGraduatedRecent: (r, days) => isGraduatedRecent(r, quotes.value, days),
    gradRecentDays: GRAD_RECENT_DAYS,
  };
});
